import { useFlagMaxLifetimeEnforcementEnabled } from "@/hooks/use-flag";
import { MaximumEnvironmentLifetimeExceededError } from "@/utils/errors";
import { ConnectError } from "@connectrpc/connect";
import type { EnvironmentMaxLifetimeExceededDetails } from "gitpod-next-api/gitpod/v1/organization_pb";
import { PolicyEnforcedFailedPreconditionDetails } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useState } from "react";

export type { EnvironmentMaxLifetimeExceededDetails };

/**
 * Encapsulates the max-lifetime enforcement flow for starting environments.
 *
 * Returns:
 * - `enforcementEnabled`: whether server-side enforcement is active
 * - `handleStartError`: call this in the catch block of startEnvironment;
 *    returns true if the error was handled (modal shown), false otherwise.
 * - `modalProps`: spread onto EnvironmentLifetimeExceededModal.
 */
export function useLifetimeEnforcement() {
    const { value: enforcementEnabled } = useFlagMaxLifetimeEnforcementEnabled();

    const [showModal, setShowModal] = useState(false);
    // Whether the modal was triggered by server-side enforcement or client-side check
    const [serverEnforced, setServerEnforced] = useState(false);
    const [lifetimeDetails, setLifetimeDetails] = useState<EnvironmentMaxLifetimeExceededDetails | undefined>();

    const handleStartError = useCallback(
        (error: unknown): boolean => {
            // Client-side check (enforcement disabled)
            if (error instanceof MaximumEnvironmentLifetimeExceededError) {
                setServerEnforced(false);
                setLifetimeDetails(undefined);
                setShowModal(true);
                return true;
            }
            // Server-side enforcement error
            if (error instanceof ConnectError) {
                const details = error.findDetails(PolicyEnforcedFailedPreconditionDetails);
                const lifetimeDetail = details.find(
                    (d) => d.policyName === "maximum_environment_lifetime_strict",
                );
                if (lifetimeDetail) {
                    setServerEnforced(true);
                    setLifetimeDetails(
                        lifetimeDetail.details.case === "maxLifetimeExceeded"
                            ? lifetimeDetail.details.value
                            : undefined,
                    );
                    setShowModal(true);
                    return true;
                }
            }
            return false;
        },
        [],
    );

    return {
        enforcementEnabled,
        handleStartError,
        modalProps: {
            open: showModal,
            onClose: () => setShowModal(false),
            serverEnforced,
            lifetimeDetails,
        },
    };
}
